/**
 * use-bootstrap-auth.ts — runs at app mount to load /api/auth/me and sync
 * the auth Zustand store with the result.
 *
 * Strategy:
 *   1. Call /me without a tenant header to learn user + tenant memberships
 *   2. If the user has at least one tenant, call /me again with X-Tenant-Id
 *      so the response includes effective permissions for that tenant.
 *      (v1: auto-select the first tenant; multi-tenant chooser is BP-future.)
 *   3. Populate or clear the auth store based on the result.
 *
 * Race-condition note: `isLoading` stays `true` until the auth store has
 * actually been synchronized with the query result. If we instead returned
 * `isLoading: false` as soon as React Query resolves, child components
 * (RequireAuth) would render on the same commit, read the *stale* store
 * (isAuthenticated: false), and Navigate to /login before the post-render
 * useEffect could call setSession. The `bootstrapped` state below closes
 * that race — it flips true only inside the effect, after the store has
 * been updated.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query';
import { ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { me } from './api';

interface BootstrapResult {
  isLoading: boolean;
  isAuthenticated: boolean;
  error: unknown;
}

export function useBootstrapAuth(): BootstrapResult {
  const setSession = useAuthStore((s) => s.setSession);
  const clearSession = useAuthStore((s) => s.clearSession);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [bootstrapped, setBootstrapped] = useState(false);

  // Step 1: who am I, what tenants do I belong to?
  const initialMe = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: () => me(),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Step 2: re-fetch with X-Tenant-Id once we know one
  const activeTenantId = initialMe.data?.tenants[0]?.id;
  const enriched = useQuery({
    queryKey: [...queryKeys.auth.me(), activeTenantId ?? '__none'],
    queryFn: () => me(activeTenantId),
    enabled: Boolean(activeTenantId),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    // 401 from the initial /me — definitively unauthenticated.
    if (initialMe.error instanceof ApiError && initialMe.error.status === 401) {
      clearSession();
      setBootstrapped(true);
      return;
    }

    // Non-401 error (network, 5xx, schema mismatch): treat as unauthenticated
    // for safety; surface the error to the UI via the hook's return value.
    if (initialMe.isError) {
      clearSession();
      setBootstrapped(true);
      return;
    }

    // Initial /me still in flight
    if (!initialMe.data) return;

    // User has tenants — wait for the enriched /me (with permissions) before
    // we say bootstrap is done.
    if (initialMe.data.tenants.length > 0) {
      if (enriched.isPending) return;
      if (enriched.isError) {
        clearSession();
        setBootstrapped(true);
        return;
      }
    }

    const resolved = enriched.data ?? initialMe.data;
    const tenant = resolved.tenants[0];
    const tenantUser = resolved.tenantUser;

    if (!tenant || !tenantUser) {
      // Authenticated but no tenant memberships (or the enriched /me did not
      // return a tenantUser for the active tenant) — UI treats as unauthenticated.
      clearSession();
    } else {
      setSession({
        user: resolved.user,
        tenantUser: {
          id: tenantUser.id,
          tenantId: tenantUser.tenantId,
          tenantName: tenant.name,
          welcomedAt: tenantUser.welcomedAt,
          celebratedMemberIds: tenantUser.celebratedMemberIds,
        },
        permissions: resolved.activeTenant?.permissions ?? [],
        canAddHumans: resolved.canAddHumans ?? false,
      });
    }
    setBootstrapped(true);
  }, [
    initialMe.data,
    initialMe.error,
    initialMe.isError,
    enriched.data,
    enriched.isError,
    enriched.isPending,
    setSession,
    clearSession,
  ]);

  return {
    isLoading: !bootstrapped,
    isAuthenticated,
    error: initialMe.error ?? enriched.error,
  };
}
